/* AppraisalComponents/hooks/useFlashingRateCascade.js
 * Iter316 · P0-1 · Flashing cascade wire-up (2026-02c).
 *
 * Mirrors `useUnderlayCascade`'s "auto-fill then self-heal" pattern:
 *   · Fires on freshly-pushed Flashing rows where `girth_mm +
 *     folds_qty + material` are all populated AND `type_name` is
 *     blank OR was previously auto-filled (guarded by
 *     `type_auto_filled` flag).
 *   · Uses the shared `resolveRateByGirthFolds` matcher — same one
 *     the Valley/Barge cascades already use — to find the smallest
 *     catalogue row with `width ≥ girth`, matching material +
 *     `${bends}f` in the name.
 *   · When a match is found, stamps `type_name`, `materials_rate`,
 *     `labour_rate`, `contractor_rate`, `buy_cost`, `supplier`,
 *     `supplier_code`, and marks `type_auto_filled: true` so an
 *     operator override survives a future diagram re-push (the
 *     effect only replaces its OWN prior auto-fills).
 *   · Does nothing on Flashing rows the operator has manually
 *     picked (type_name set + type_auto_filled falsy).
 *   · Rounding: "next catalogue width ≥ girth" — 370mm resolves to
 *     the smallest matching row with width ≥ 370 (typically 400W
 *     on standard 50mm steps).
 *   · Folds: `folds_qty` = bends between segments (2 for a
 *     [20,330,20] fold array). Falls back to `folds.length - 1`
 *     when `folds_qty` isn't explicitly set.
 *
 * State stays in the parent · this hook takes state slices + setGroups.
 */
import { useEffect } from 'react';
import { resolveRateByGirthFolds, deriveFoldsQty } from './flashingRateResolver';

export function useFlashingRateCascade({
  rates,
  groupsFlashing, // groups.Flashing
  primaryMaterial, // appraisal-level fallback when the row has no `material`
  setGroups,
}) {
  useEffect(() => {
    const flashingRates = rates?.Flashing || [];
    if (!flashingRates.length) return;
    const rows = groupsFlashing || [];
    if (!rows.length) return;

    let touched = false;
    const next = rows.map((r) => {
      // Skip rows the operator has manually picked (type_name set + not
      // marked as auto-filled by us).
      if (r.type_name && !r.type_auto_filled) return r;
      const material = r.material || primaryMaterial || '';
      const girth = Number(r.girth_mm) || 0;
      const bends = deriveFoldsQty(r);
      if (!material || girth <= 0 || bends <= 0) return r;
      const match = resolveRateByGirthFolds(material, girth, bends, flashingRates);
      if (!match) return r;
      // Idempotent — skip when the resolved row is already applied.
      if (r.type_name === match.type_name) return r;
      touched = true;
      return {
        ...r,
        type_name:       match.type_name || '',
        materials_rate:  Number(match.materials_rate)  || 0,
        labour_rate:     Number(match.labour_rate)     || 0,
        contractor_rate: Number(match.contractor_rate) || 0,
        buy_cost:        Number(match.buy_cost)        || 0,
        supplier:        match.supplier || '',
        supplier_code:   match.supplier_code || '',
        type_auto_filled: true,
        type_auto_source: { material, girth_mm: girth, folds_qty: bends },
      };
    });

    if (touched) setGroups((prev) => ({ ...prev, Flashing: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates?.Flashing, groupsFlashing, primaryMaterial]);
}
